import React, { useState, useEffect } from 'react';
import b2bService from '../../services/b2bService';
import B2bPortalReportModal from './B2bPortalReportModal';
import './B2bPortalOrderHistoryPage.css';

const B2bPortalOrderHistoryPage = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 60);
    return d.toISOString().split('T')[0];
  });
  const [toDate, setToDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 30);
    return d.toISOString().split('T')[0];
  });
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const data = await b2bService.getPortalOrders({
        fromDate,
        toDate,
        search,
        offset: 0,
        limit: 100
      });
      setOrders(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching portal orders:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, [fromDate, toDate]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchOrders();
  };

  const handleViewReport = async (order) => {
    const labNo = order.ledgerTransactionNo || order.LedgerTransactionNo;
    setReportLoading(true);
    try {
      const data = await b2bService.getPortalReportDetails(labNo);
      const patientInfo = data?.patient || data?.item1 || {
        ledgerTransactionNo: labNo,
        patientName: order.patientName || order.PatientName,
        uhid: order.uhid || order.UHID,
        gender: order.gender || order.Gender,
        ageYear: order.ageYear || order.AgeYear,
        doctorName: order.doctorName,
        centreName: order.centreName
      };

      const testItems = data?.tests || data?.item2 || [];

      setSelectedReport({
        patient: patientInfo,
        tests: testItems,
        orderRef: labNo
      });
    } catch (err) {
      // never show a made-up report: a failed fetch is reported as a failure
      alert(err.response?.data?.message || 'Unable to load the report. Please try again.');
    } finally {
      setReportLoading(false);
    }
  };

  // Compute summary metrics for top strip
  const totalOrdersCount = orders.length;
  const authorizedCount = orders.filter(
    (o) => (o.approvedTestCount || o.ApprovedTestCount || 0) >= (o.testCount || o.TestCount || 1) && (o.testCount || o.TestCount || 0) > 0
  ).length;
  const underReviewCount = totalOrdersCount - authorizedCount;
  const totalValue = orders.reduce((acc, o) => acc + Number(o.netAmount || o.NetAmount || 0), 0);

  return (
    <div className="b2b-orders-container">
      {/* Header */}
      <div className="b2b-dashboard-header">
        <div>
          <h2 className="b2b-dashboard-title">
            <span style={{ fontSize: '24px' }}>📋</span> Orders &amp; Clinical Reports
          </h2>
          <div className="b2b-dashboard-subtitle">
            Zero-Trust Protected Patient Accession Feed. Showing bookings isolated exclusively to your contracted account.
          </div>
        </div>
        <button className="b2b-refresh-btn" onClick={fetchOrders}>
          <span>🔄</span> Refresh Accessions
        </button>
      </div>

      {/* Accession Summary KPI Cards */}
      <div className="b2b-orders-kpi-grid">
        <div className="b2b-kpi-card">
          <div className="b2b-kpi-label">Total Filtered Accessions</div>
          <div className="b2b-kpi-value">{totalOrdersCount}</div>
          <div className="b2b-kpi-sub">In selected date window</div>
        </div>
        <div className="b2b-kpi-card">
          <div className="b2b-kpi-label">Authorized &amp; Signed Off</div>
          <div className="b2b-kpi-value" style={{ color: '#059669' }}>{authorizedCount}</div>
          <div className="b2b-kpi-sub">Ready for clinical download</div>
        </div>
        <div className="b2b-kpi-card">
          <div className="b2b-kpi-label">In Testing / Review</div>
          <div className="b2b-kpi-value" style={{ color: '#d97706' }}>{underReviewCount}</div>
          <div className="b2b-kpi-sub">Analytic turnaround in progress</div>
        </div>
        <div className="b2b-kpi-card">
          <div className="b2b-kpi-label">Total Contracted Volume</div>
          <div className="b2b-kpi-value" style={{ color: '#2563eb' }}>
            ₹{totalValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div className="b2b-kpi-sub">Panel contract billing sum</div>
        </div>
      </div>

      {/* Filter Toolbar */}
      <form className="b2b-filter-bar" onSubmit={handleSearchSubmit}>
        <div className="b2b-filter-group">
          <label className="b2b-filter-label">From Date</label>
          <input
            type="date"
            className="b2b-filter-input"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
          />
        </div>

        <div className="b2b-filter-group">
          <label className="b2b-filter-label">To Date</label>
          <input
            type="date"
            className="b2b-filter-input"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
          />
        </div>

        <div className="b2b-filter-group" style={{ flex: '1', minWidth: '240px' }}>
          <label className="b2b-filter-label">Search Query</label>
          <input
            type="text"
            className="b2b-filter-input"
            placeholder="Search Patient Name, UHID, Lab No..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <button type="submit" className="b2b-filter-submit-btn">
          🔍 Search Filter
        </button>
      </form>

      {/* Orders Table */}
      <div className="b2b-table-card">
        {loading ? (
          <div style={{ padding: '50px', textAlign: 'center', color: '#64748b' }}>
            <div style={{ fontSize: '24px', marginBottom: '8px' }}>⏳</div>
            Loading authenticated accessions...
          </div>
        ) : orders.length === 0 ? (
          <div style={{ padding: '60px 20px', textAlign: 'center', color: '#64748b' }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>📂</div>
            <div style={{ fontSize: '16px', fontWeight: '700', color: '#1e293b' }}>
              No accessions found for the specified period and criteria.
            </div>
            <div style={{ fontSize: '13px', marginTop: '6px' }}>
              Try broadening the date filter or clear search keywords.
            </div>
          </div>
        ) : (
          <table className="b2b-data-table">
            <thead>
              <tr>
                <th>Booking Date</th>
                <th>Order Ref #</th>
                <th>Patient Details</th>
                <th>Diagnostic Tests</th>
                <th>Contract Net (₹)</th>
                <th>Sign-off Status</th>
                <th style={{ textAlign: 'center' }}>Clinical Report</th>
              </tr>
            </thead>
            <tbody>
              {orders.map((o, idx) => {
                const labNo = o.ledgerTransactionNo || o.LedgerTransactionNo;
                const pName = o.patientName || o.PatientName || o.pName || o.PName;
                const uhid = o.uhid || o.UHID || o.patient_ID || o.Patient_ID;
                const gender = o.gender || o.Gender || 'Other';
                const age = o.ageYear || o.AgeYear || '--';
                const bookingDate = o.bookingDate || o.orderDate || o.Date;
                const netAmount = Number(o.netAmount || o.NetAmount || 0);
                const testCount = o.testCount || o.TestCount || 1;
                const approvedCount = o.approvedTestCount || o.ApprovedTestCount || 0;
                const isFullyApproved = approvedCount >= testCount && testCount > 0;
                const isPartiallyApproved = approvedCount > 0 && !isFullyApproved;

                return (
                  <tr key={o.ledgerTransactionID || o.LedgerTransactionID || idx}>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <div style={{ fontWeight: '600', color: '#0f172a' }}>
                        {bookingDate ? new Date(bookingDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : 'Today'}
                      </div>
                      <div style={{ fontSize: '11px', color: '#94a3b8' }}>
                        {bookingDate ? new Date(bookingDate).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : ''}
                      </div>
                    </td>
                    <td>
                      <span className="b2b-ord-tag">{labNo}</span>
                      {o.clientOrderNumber && (
                        <div style={{ fontSize: '11px', color: '#64748b', marginTop: '3px' }}>
                          Client Ref: {o.clientOrderNumber}
                        </div>
                      )}
                    </td>
                    <td>
                      <div className="b2b-pat-info">
                        <div className="b2b-pat-avatar">
                          {pName ? pName.replace(/^(Mr\.|Mrs\.|Ms\.|Dr\.|Baby|Master)\s*/i, '').charAt(0) : 'P'}
                        </div>
                        <div>
                          <div style={{ fontWeight: '700', color: '#0f172a' }}>{pName}</div>
                          <div style={{ fontSize: '11px', color: '#64748b' }}>
                            {uhid} • {gender}, {age} Y
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div style={{ fontWeight: '600', color: '#334155' }}>
                        {testCount} Investigation{testCount > 1 ? 's' : ''}
                      </div>
                      <div style={{ fontSize: '11px', color: '#64748b' }}>
                        {approvedCount} of {testCount} Authorized
                      </div>
                    </td>
                    <td style={{ fontWeight: '800', color: '#0f172a', whiteSpace: 'nowrap' }}>
                      ₹{netAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </td>
                    <td>
                      {isFullyApproved ? (
                        <span className="b2b-status-tag authorized">
                          ✓ Authorized
                        </span>
                      ) : isPartiallyApproved ? (
                        <span className="b2b-status-tag partial">
                          ⏳ Partial ({approvedCount}/{testCount})
                        </span>
                      ) : (
                        <span className="b2b-status-tag testing">
                          🧪 In Testing
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {isFullyApproved ? (
                        <button
                          className="b2b-btn-view-doc"
                          onClick={() => handleViewReport(o)}
                          disabled={reportLoading}
                        >
                          👁️ View Report
                        </button>
                      ) : (
                        <span className="b2b-report-locked" title="Report opens after the pathologist approves every test">
                          🔒 Awaiting approval
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {selectedReport && (
        <B2bPortalReportModal report={selectedReport} onClose={() => setSelectedReport(null)} />
      )}
    </div>
  );
};

export default B2bPortalOrderHistoryPage;
